// The delta-v map's COURSE FIX: the burn that puts a ship already under way
// back on the arrival the picked plan named, and the direction to point it.
//
// The number is not new. It is the same trim solve `src/sim/autopilot.ts` flies
// between its own rungs, and it reached the panel only through
// `autopilotFlightLine()` in src/main.ts, which is null until GO has flown. So a
// player flying the map's plan by hand had the two burns the panel prints and
// nothing else, and a departure that leaves the asymptote degrees off misses
// Mars by half an AU from there. This runs that solve off the live ship state
// and hands its answer to the panel and to the view's burn marker.
//
// No DOM and no three.js: `src/hudDvMap.ts` writes the row and
// `src/scene/progradeMarkers.ts` draws the direction. `correctionRowText` is
// here rather than beside the panel's own formatters so a headless check can
// read the words a player reads - `tools/preview/transit-check.mjs --hand`
// flies the corrections off this module and the row's own text.
//
// Nothing here flies anything. It never touches the flight state, never asks
// for the stick, and never calls `createAutopilot`.
//
// The figure is what the solve REACHED, not a converged answer: it walks toward
// the velocity the arrival wants inside a per-call step budget, and the first
// look after a burn can stop a long way short of it. What the solve predicts it
// would miss the arrival by is on `state().missM`, which
// `tools/preview/transit-check.mjs --hand` prints beside every look; what makes
// the trip come out is the ladder of looks, the same ladder the autopilot's own
// trim rungs are.

import {
  SOLVE_MS_BUDGET, SOLVE_STEP_BUDGET, TRIM_DEADBAND_MPS, arrivalStandoffFor,
  captureAimPeriapsisM, createModel, departureIsTimed, departureOrbitPeriodS, departureOriginId,
  departureTimingReachS, periapsisAimFor, periapsisInS, plannedParkingAltitudeM, scheduledDepartureStartJd,
  timedDepartureStartJd,
} from './autopilot.ts';
import type { AutopilotEnv, GuidanceModel, GuidanceSolve } from './autopilot.ts';
import type { DvMapMoved } from './dvMap.ts';
import type { FlightVec } from './flight.ts';
import { finiteCaptureOf } from './transfer.ts';
import type { TransferBurn, TransferPlan } from './transfer.ts';

const SECONDS_PER_DAY = 86400;

/** Metres a second of excess speed that re-prices the capture's lead. Pricing
 *  it integrates the whole burn, a few milliseconds, and a metre a second
 *  moves the lead about a second. */
const BRAKE_REPRICE_MPS = 1;

/**
 * Real milliseconds between one answer landing and the next solve opening.
 *
 * The same floor `src/sim/dvMap.ts` re-plans the whole survey on, for the same
 * reason: the trigger below fires on a burn, and a burn is every frame the
 * engine is open.
 */
const RESOLVE_FLOOR_MS = 250;

/** Metres a second of heliocentric speed change that re-opens the solve: a burn. */
const RESOLVE_SPEED_MPS = 5;

/**
 * Departure burns the DEPARTURE marker stays on the view for, counted from the
 * plan's own departure epoch and measured at the thrust the map priced that
 * burn at.
 *
 * A departure is not all engine. The ship turns onto the marker, holds there
 * while the climb bends its own motion off it, turns back onto it, and a turn
 * under warp costs simulated seconds; Earth to Mars the whole departure ends
 * 1.7 burn lengths past the epoch against one burn length of engine. The
 * marker has to outlast that or the half of the departure that most needs it
 * is flown with nothing on the view to aim at. Three is that with room, and it
 * errs long on purpose: too long costs an hour of coast where the row prices a
 * fix and the view still draws the departure, too short costs the departure.
 */
const DEPARTURE_MARKER_BURNS = 3;


/** Which direction the marker is drawing, for the label beside it. */
export type DvCorrectionMarkerKind = 'departure' | 'fix';

/** Where to point the nose, in the axes the flight state's own velocity is in. */
export interface DvCorrectionMarker {
  readonly kind: DvCorrectionMarkerKind;
  /** Unit. The heliocentric axes, which the flight frame shares. */
  readonly dir: { readonly x: number; readonly y: number; readonly z: number };
  /** What the view writes beside the glyph. */
  readonly label: string;
}

export interface DvCorrectionState {
  /** A plan is picked, the map plans from where the ship is, and GO has not flown. */
  readonly armed: boolean;
  /** Armed, but the plan's own departure has not come round yet. */
  readonly pending: boolean;
  readonly destinationName: string;
  /** Metres a second the fix wants. 0 with nothing to fix and while unarmed. */
  readonly mps: number;
  /** Simulated seconds from now to the plan's arrival; 0 with no plan. */
  readonly timeToGoS: number;
  /** Simulated seconds from now until the capture burn's throttle opens, timed
   *  the way `src/sim/autopilot.ts` times its own brake: off the finite burn's
   *  own lead for a capture into orbit, off the match law's closing gap for a
   *  moon capture. NaN before the trip has left, while something else has the
   *  stick, or where neither timing applies (no capture at all, or a capture
   *  that only ever stops on contact). */
  readonly brakeInS: number;
  /** Simulated seconds from now until a hand-flown departure burn should
   *  open, timed the same way GO times its own departure. NaN before a plan
   *  is armed, once the burn has been detected, or where the ship state or
   *  origin orbit needed to time it is not available. */
  readonly departInS: number;
  /** What the view draws, or null while it draws nothing. */
  readonly marker: DvCorrectionMarker | null;
  /** A solve is part way through. The row keeps its last answer meanwhile. */
  readonly solving: boolean;
  /** Answers landed since boot, so a check can wait for a fresh one. */
  readonly answers: number;
  /** Metres the last landed solve predicted it would miss the arrival by, its
   *  own `lastMissM`. NaN before the first one. Read by the checks; the row
   *  does not print it. */
  readonly missM: number;
  /** TDB Julian date the answer in hand was seeded at. */
  readonly seedJd: number;
  /** `?dvcorrectionoff=1`: the row blank and the marker gone. */
  readonly off: boolean;
}

export interface DvCorrectionConfig {
  /** The plan picked on the map, which is what GO would fly. Null with none. */
  plan: () => TransferPlan | null;
  /** The map plans from where the ship is and nothing else has the stick. */
  live: () => boolean;
  /** The ship's heliocentric metres. */
  shipAt: () => FlightVec | null;
  /** The ship's heliocentric metres a second. */
  shipVelocity: () => FlightVec | null;
  /** TDB Julian date. */
  epochJd: () => number;
  /** Metres a second squared at full throttle, `flightConfig.accel`: the same
   *  thrust the map priced the departure's own burn length at, and so how long
   *  the DEPARTURE marker has to stay drawn. */
  accelMps2: () => number;
  /** The registry as the solve looks it up: `src/main.ts`'s own autopilot env. */
  env: AutopilotEnv;
  /** How far from the destination's centre the arrival is aimed, metres.
   *  Absent takes the destination's own, the way the autopilot's trims do. */
  standoffM?: number;
  /** The parking altitude the map this plan came off priced its capture into,
   *  metres, which is the orbit that aim is the surface plus. Absent takes
   *  the plan's own, as the autopilot's does. */
  parkingAltitudeM?: number;
  /** Metres a second under which the row says there is nothing to fix. */
  deadbandMps?: number;
  /** Integrator steps one `update()` may spend, and the milliseconds with them.
   *  The autopilot's own budget: the panel may not stall a frame either. */
  stepBudget?: number;
  msBudget?: number;
  /**
   * The control (`?dvcorrectionoff=1`): the row blank and the marker hidden,
   * which is the panel as it shipped before this row existed. A hand pilot
   * under it has the two burns and nothing else, which is what
   * `transit-check.mjs --inject no-correction-row` and the dojo's own leg have
   * to catch.
   */
  off?: boolean;
  /**
   * The control (`?dvcorrectionholdoutlived=1`): hold the picked plan past its
   * own arrival, the way the row read before that was dropped. A player who
   * picks a row months before the window, waits while the map walks on to a
   * cheaper one and then flies THAT, gets a row and a marker that never come
   * back, which is what `--transit --inject hold-outlived-plan` has to catch.
   */
  holdOutlived?: boolean;
  /**
   * The control `tools/preview/transit-check.mjs --inject marker-ends-at-epoch`
   * restores: the DEPARTURE marker comes off at the departure epoch, the way it
   * did before it was held through the burn, so a pilot pushing onto it flies
   * the whole push with nothing drawn.
   */
  markerEndsAtEpoch?: boolean;
  /**
   * The control (`?dvcorrectionholdbyarrival=1`): the row re-taken off the held
   * plan's own ARRIVAL epoch alone, which is how it read before it followed the
   * map's hand-off: a window that passes with nothing flown on it leaves the row
   * pricing a fix toward the crossing that left, from a parking orbit the ship
   * never left. What `--transit --inject hold-by-arrival` has to catch.
   */
  holdByArrival?: boolean;
  /**
   * The crossing `src/sim/dvMap.ts` gave up and the one it armed in its place,
   * a fresh object per hand-off. The row follows THAT and not every survey: the
   * map re-prices its own pick every few hundred milliseconds once the armed
   * departure has passed, and a row re-taken on each of those never lands an
   * answer. The map says nothing here while the trip is being flown, which is
   * what keeps the plan through a flight and a cancel.
   */
  handedOn?: () => DvMapMoved | null;
  /**
   * The control `tools/preview/transit-check.mjs --inject aim-on-approach-line`
   * restores: the row's own aim back to the destination pulled back along the
   * approach by the standoff, the point `src/sim/autopilot.ts`'s trims aimed at
   * before `periapsisAimFor`. Nothing a player reaches sets it.
   */
  aimOnApproachLine?: boolean;
  /** As `AutopilotInput.burnThrustShare`: the share of the drive a heat cap
   *  leaves the departure's climb, so the timing matches what actually burns.
   *  Undefined runs the whole drive, `timedDepartureStartJd`'s own default. */
  burnThrustShare?: () => number;
  /**
   * The control (`?dvcorrectionuntimeddeparture=1`): count to the schedule
   * alone instead of timing an escape departure, the way a bound departure
   * already does. Nothing a player reaches sets it.
   */
  untimedDeparture?: boolean;
}

export interface DvCorrection {
  /** Once a frame. Cheap while nothing is armed; a slice of a solve otherwise. */
  update: () => void;
  state: () => DvCorrectionState;
  dispose: () => void;
}

/** The em dash every panel row shows for "nothing to say", by code point so
 *  this file stays plain ASCII. */
export const CORRECTION_NOTHING = String.fromCodePoint(0x2014);

/** The marker's own words, which name what the glyph is pointing at. */
const MARKER_LABELS: Readonly<Record<DvCorrectionMarkerKind, string>> = {
  departure: 'DEPARTURE',
  fix: 'COURSE FIX',
};

const IDLE: DvCorrectionState = {
  armed: false, pending: false, destinationName: '', mps: 0, timeToGoS: 0, brakeInS: Number.NaN,
  departInS: Number.NaN, marker: null, solving: false, answers: 0, missM: Number.NaN, seedJd: 0, off: false,
};

/** A speed change in whichever unit reads without leading zeros, the same way
 *  the panel's other delta-v cells are written. */
function dvFigure(mps: number): string {
  if (mps >= 1000) return `${(mps / 1000).toFixed(3)} km/s`;
  return `${mps.toFixed(0)} m/s`;
}

/** Simulated seconds in the largest unit that still says something. */
function tgoFigure(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'now';
  if (seconds < 120) return `${Math.round(seconds)} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) return `${(seconds / 3600).toFixed(1)} hr`;
  const days = seconds / SECONDS_PER_DAY;
  if (days >= 10) return `${Math.round(days)} days`;
  return `${days.toFixed(1)} days`;
}

/**
 * The row's own text: what a player reads off the panel, and what
 * `tools/preview/transit-check.mjs --hand` parses its corrections out of.
 */
export function correctionRowText(state: DvCorrectionState): string {
  if (!state.armed || state.off) return CORRECTION_NOTHING;
  const brake = !Number.isFinite(state.brakeInS) ? null
    : state.brakeInS > 0 ? `capture burn starts in ${tgoFigure(state.brakeInS)}`
      : 'capture burn due now';
  if (!(state.mps > 0)) return brake ?? CORRECTION_NOTHING;
  const fix = `${dvFigure(state.mps)}, arrival in ${tgoFigure(state.timeToGoS)}`;
  return brake === null ? fix : `${fix}, ${brake}`;
}

/** The DEPARTURE row's own text, off `DvCorrectionState.departInS`. */
export function departureRowText(state: DvCorrectionState): string {
  if (Number.isNaN(state.departInS)) return CORRECTION_NOTHING;
  return state.departInS > 0 ? `burn starts in ${tgoFigure(state.departInS)}` : 'start burn now';
}

function unit(x: number, y: number, z: number): { x: number; y: number; z: number } | null {
  const l = Math.hypot(x, y, z);
  if (!(l > 0)) return null;
  return { x: x / l, y: y / l, z: z / l };
}

/** TDB Julian date the DEPARTURE marker comes off the view: the plan's own
 *  departure epoch plus the burn that opens there, flown at the ship's thrust.
 *  The epoch itself where there is no burn to fly or no thrust to fly it at. */
function departureMarkerEndJd(
  plan: TransferPlan, departure: TransferBurn | null, accelMps2: number,
): number {
  if (departure === null || !(accelMps2 > 0)) return plan.departureEpochJd;
  const burnS = (departure.dvKmS * 1000) / accelMps2;
  return plan.departureEpochJd + (DEPARTURE_MARKER_BURNS * burnS) / SECONDS_PER_DAY;
}

export function createDvCorrection(config: DvCorrectionConfig): DvCorrection {
  /** Null takes the destination's own, the way the autopilot's trims do. */
  const fixedStandoffM = config.standoffM ?? null;
  const deadbandMps = config.deadbandMps ?? TRIM_DEADBAND_MPS;
  const stepBudget = config.stepBudget ?? SOLVE_STEP_BUDGET;
  const msBudget = config.msBudget ?? SOLVE_MS_BUDGET;
  const off = config.off === true;
  const holdOutlived = config.holdOutlived === true;
  const markerEndsAtEpoch = config.markerEndsAtEpoch === true;
  const holdByArrival = config.holdByArrival === true;
  const handedOnOf = config.handedOn ?? ((): DvMapMoved | null => null);

  /** The plan the row is answering for. Live mode re-plans the survey every few
   *  hundred milliseconds and its next window walks forward with the clock, so
   *  the plan is taken once per pick and held: a course cannot be strayed from
   *  while the map keeps drawing a fresh one. Picking the row again re-takes it,
   *  and so does the map handing its own pick on to another crossing. */
  let held: TransferPlan | null = null;
  let heldId: string | null = null;
  /** The last hand-off seen. The line stays on the panel until the next pick,
   *  so identity is the edge and the row re-takes once. */
  let seenHandOff: DvMapMoved | null = null;
  let model: GuidanceModel | null = null;
  let solve: GuidanceSolve | null = null;
  let answers = 0;
  let answerMps = 0;
  let answerDir: { x: number; y: number; z: number } | null = null;
  let answerMissM = Number.NaN;
  let seedJd = 0;
  let seedSpeedMps = 0;
  let landedRealMs = -Infinity;
  let solveSeedJd = 0;
  const seedVelocity: FlightVec = { x: 0, y: 0, z: 0 };
  const aim: FlightVec = { x: 0, y: 0, z: 0 };
  const destAt: FlightVec = { x: 0, y: 0, z: 0 };
  const destVel: FlightVec = { x: 0, y: 0, z: 0 };
  const rel: FlightVec = { x: 0, y: 0, z: 0 };
  const relVel: FlightVec = { x: 0, y: 0, z: 0 };
  const aimOffset: FlightVec = { x: 0, y: 0, z: 0 };
  const brakeAt: FlightVec = { x: 0, y: 0, z: 0 };
  const brakeVel: FlightVec = { x: 0, y: 0, z: 0 };
  const matchOriginAt: FlightVec = { x: 0, y: 0, z: 0 };
  const matchOriginVel: FlightVec = { x: 0, y: 0, z: 0 };
  const matchVel: FlightVec = { x: 0, y: 0, z: 0 };
  /** The capture's lead last priced, and the excess speed it was priced at. */
  let leadVInfMps = Number.NaN;
  let leadS = Number.NaN;

  /** The body the held plan's departure climbs out of. */
  let departOriginId = 'sol';
  /** Where the schedule centres the push, before any timing. */
  let departScheduledJd = 0;
  /** The timed (or still-scheduled) start the row counts down to. Moves on a
   *  whole orbit at a time when a window passes unburned. */
  let departStartJd = 0;
  /** The one attempt at timing (or the untimed no-op) for the current
   *  `departStartJd`, cleared to try again off a fresh ship state. */
  let departTimedOnce = false;
  /** Specific orbital energy and origin-relative speed recorded the moment
   *  the current `departStartJd` was set, the baseline a real burn moves off. */
  let departEnergy0 = Number.NaN;
  let departRelSpeed0 = Number.NaN;
  /** A real burn was seen, or a timed departure priced out past what it can
   *  carry: the row goes blank and stays blank until the plan is re-taken. */
  let departBurnStarted = false;
  /** What `state()` reads for `departInS`. */
  let departInSValue = Number.NaN;

  function forget(): void {
    held = null;
    heldId = null;
    model = null;
    solve = null;
    leadVInfMps = Number.NaN;
    leadS = Number.NaN;
    answerMps = 0;
    answerDir = null;
    answerMissM = Number.NaN;
    seedJd = 0;
    landedRealMs = -Infinity;
    departOriginId = 'sol';
    departScheduledJd = 0;
    departStartJd = 0;
    departTimedOnce = false;
    departEnergy0 = Number.NaN;
    departRelSpeed0 = Number.NaN;
    departBurnStarted = false;
    departInSValue = Number.NaN;
  }

  /** Specific orbital energy relative to `originId`, at `jd`: `v^2/2 - gm/r`.
   *  Constant on a coast; what a real burn moves. */
  function departEnergyAt(originId: string, jd: number, at: FlightVec, velocity: FlightVec): number {
    config.env.bodyAt(originId, jd, matchOriginAt);
    config.env.bodyVelocityAt(originId, jd, matchOriginVel);
    const rx = at.x - matchOriginAt.x;
    const ry = at.y - matchOriginAt.y;
    const rz = at.z - matchOriginAt.z;
    const vx = velocity.x - matchOriginVel.x;
    const vy = velocity.y - matchOriginVel.y;
    const vz = velocity.z - matchOriginVel.z;
    const gm = config.env.gmOf(originId);
    return (vx * vx + vy * vy + vz * vz) / 2 - gm / Math.hypot(rx, ry, rz);
  }

  /** Speed relative to `originId`, at `jd`: what a 5 m/s trigger scales by,
   *  since `dE ~= v*dv` linearises the energy a small burn moves. */
  function departRelSpeedAt(originId: string, jd: number, velocity: FlightVec): number {
    config.env.bodyVelocityAt(originId, jd, matchOriginVel);
    return Math.hypot(
      velocity.x - matchOriginVel.x, velocity.y - matchOriginVel.y, velocity.z - matchOriginVel.z);
  }

  /**
   * The pre-departure countdown: computed off the ship's own orbit before the
   * course-fix solve below ever opens, since that solve only starts once the
   * plan's departure has already happened. Held closure state so a window
   * that passes unburned can move the timed start on a whole orbit, and a
   * real burn (an energy change past the trigger) blanks the row for good.
   */
  function updateDeparture(plan: TransferPlan, jd: number): void {
    if (departBurnStarted) return;
    const at = config.shipAt();
    const velocity = config.shipVelocity();
    if (at === null || velocity === null) {
      departInSValue = Number.NaN;
      return;
    }
    const accel = config.accelMps2();
    const period = departureOrbitPeriodS(config.env, departOriginId, jd, at, velocity);
    if (Number.isNaN(period)) {
      departInSValue = Number.NaN;
      return;
    }
    const toStartS = (departStartJd - jd) * SECONDS_PER_DAY;
    if (toStartS > departureTimingReachS(period)) {
      departInSValue = toStartS;
      return;
    }
    const timed = departureIsTimed(plan, accel) && config.untimedDeparture !== true;
    if (!departTimedOnce) {
      departTimedOnce = true;
      if (timed) {
        const result = timedDepartureStartJd(
          plan, config.env, accel, { jd, at, velocity, burnThrustShare: config.burnThrustShare?.() },
          period, 0, departStartJd);
        if (typeof result === 'string') {
          // Priced out past what the departure can carry from here: nothing
          // to count down to until the plan is re-taken.
          departBurnStarted = true;
          departInSValue = Number.NaN;
          return;
        }
        departStartJd = result;
        departEnergy0 = departEnergyAt(departOriginId, jd, at, velocity);
        departRelSpeed0 = departRelSpeedAt(departOriginId, jd, velocity);
      }
    }
    const windowS = period / 120;
    const burned = !Number.isNaN(departEnergy0)
      && Math.abs(departEnergyAt(departOriginId, jd, at, velocity) - departEnergy0)
        > departRelSpeed0 * RESOLVE_SPEED_MPS;
    if (burned) {
      if (jd < departStartJd - windowS / SECONDS_PER_DAY) {
        // Well before the window even opens: a stale read, not a burn. Time
        // again next frame off the ship's current state.
        departTimedOnce = false;
        departEnergy0 = Number.NaN;
        departRelSpeed0 = Number.NaN;
        departInSValue = toStartS;
        return;
      }
      departBurnStarted = true;
      departInSValue = Number.NaN;
      return;
    }
    if (jd > departStartJd + windowS / SECONDS_PER_DAY) {
      if (timed) {
        departStartJd += period / SECONDS_PER_DAY;
        departInSValue = (departStartJd - jd) * SECONDS_PER_DAY;
        return;
      }
      const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
      if (jd >= departureMarkerEndJd(plan, departure, accel)) {
        departInSValue = Number.NaN;
        return;
      }
      departInSValue = 0;
      return;
    }
    departInSValue = (departStartJd - jd) * SECONDS_PER_DAY;
  }

  /** The aim the autopilot's own trims use: the periapsis of the approach at
   *  the height the capture is priced from where the arrival captures into
   *  orbit, the destination pulled
   *  back along the approach by it where the arrival only matches velocity. */
  function aimPoint(plan: TransferPlan, jd: number, at: FlightVec, velocity: FlightVec): void {
    config.env.bodyAt(plan.destinationId, plan.arrivalEpochJd, aim);
    const standoffM = fixedStandoffM
      ?? arrivalStandoffFor(
        config.env.radiusOf(plan.destinationId), config.parkingAltitudeM ?? plannedParkingAltitudeM(plan));
    if (!(standoffM > 0)) return;
    config.env.bodyVelocityAt(plan.destinationId, jd, destVel);
    const capture = plan.burns.find((b) => b.kind === 'capture') ?? null;
    if (capture !== null && capture.frame === 'body-relative' && config.aimOnApproachLine !== true) {
      config.env.bodyAt(plan.destinationId, jd, destAt);
      rel.x = at.x - destAt.x;
      rel.y = at.y - destAt.y;
      rel.z = at.z - destAt.z;
      relVel.x = velocity.x - destVel.x;
      relVel.y = velocity.y - destVel.y;
      relVel.z = velocity.z - destVel.z;
      const gm = config.env.gmOf(plan.destinationId);
      const periapsisM = captureAimPeriapsisM(
        capture, standoffM, { rel, relVel, gm, accelMps2: config.accelMps2() });
      if (periapsisAimFor(rel, relVel, gm, periapsisM, capture.vInfKmS * 1000, aimOffset)) {
        aim.x += aimOffset.x;
        aim.y += aimOffset.y;
        aim.z += aimOffset.z;
        return;
      }
    }
    const dx = velocity.x - destVel.x;
    const dy = velocity.y - destVel.y;
    const dz = velocity.z - destVel.z;
    const l = Math.hypot(dx, dy, dz);
    if (!(l > 0)) return;
    aim.x -= (dx / l) * standoffM;
    aim.y -= (dy / l) * standoffM;
    aim.z -= (dz / l) * standoffM;
  }

  function speedOf(v: FlightVec): number {
    return Math.hypot(v.x, v.y, v.z);
  }

  /** The velocity a ship at `at` needs to ride `destinationId`'s own orbit
   *  about `parentId`: the same vis-viva match `src/sim/autopilot.ts`'s
   *  `coOrbitVelocity` steers a moon capture onto, kept here so the row can
   *  time that rung without a flying autopilot to read it off. */
  function matchOrbitVelocity(
    destinationId: string, parentId: string, jd: number, at: FlightVec, out: FlightVec,
  ): boolean {
    config.env.bodyAt(destinationId, jd, destAt);
    config.env.bodyVelocityAt(destinationId, jd, destVel);
    const gm = config.env.gmOf(parentId);
    config.env.bodyAt(parentId, jd, matchOriginAt);
    config.env.bodyVelocityAt(parentId, jd, matchOriginVel);
    const mx = destAt.x - matchOriginAt.x;
    const my = destAt.y - matchOriginAt.y;
    const mz = destAt.z - matchOriginAt.z;
    const ux = destVel.x - matchOriginVel.x;
    const uy = destVel.y - matchOriginVel.y;
    const uz = destVel.z - matchOriginVel.z;
    const rm = Math.hypot(mx, my, mz);
    const vm = Math.hypot(ux, uy, uz);
    const rs = Math.hypot(at.x - matchOriginAt.x, at.y - matchOriginAt.y, at.z - matchOriginAt.z);
    const want = gm * (2 / rs - (2 / rm - (vm * vm) / gm));
    if (!(gm > 0) || !(rm > 0) || !(vm > 0) || !(rs > 0) || !(want > 0)) return false;
    const k = Math.sqrt(want) / vm;
    out.x = matchOriginVel.x + ux * k;
    out.y = matchOriginVel.y + uy * k;
    out.z = matchOriginVel.z + uz * k;
    return true;
  }

  /** Seconds until the capture's throttle opens, the way `src/sim/autopilot.ts`
   *  times its brake once its last rung is flown: the finite capture re-priced
   *  at the excess speed the ship brings, ahead of the periapsis it is falling
   *  to, inside the destination's sphere; the plan's own lead ahead of the
   *  arrival epoch outside it; a moon capture, which prices no finite burn,
   *  timed instead off the match law's own closing gap to the destination's
   *  orbital speed. */
  function brakeInS(plan: TransferPlan, jd: number): number {
    const capture = plan.burns.find((b) => b.kind === 'capture') ?? null;
    const finite = capture?.finiteCapture;
    if (finite === undefined) {
      if (capture === null || capture.frame === 'body-relative') return Number.NaN;
      const at = config.shipAt();
      const velocity = config.shipVelocity();
      const accel = config.accelMps2();
      if (at === null || velocity === null || !(accel > 0)) return Number.NaN;
      const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
      const originId = departure?.localBurn?.referenceId ?? departure?.referenceId ?? 'sol';
      const parentId = (departure?.boundSemiMajorKm ?? 0) > 0 ? originId : 'sol';
      if (!matchOrbitVelocity(plan.destinationId, parentId, jd, at, matchVel)) return Number.NaN;
      const gap = Math.hypot(matchVel.x - velocity.x, matchVel.y - velocity.y, matchVel.z - velocity.z);
      return (plan.arrivalEpochJd - jd) * SECONDS_PER_DAY - gap / (2 * accel);
    }
    const planned = (plan.arrivalEpochJd - jd) * SECONDS_PER_DAY - finite.leadS;
    const at = config.shipAt();
    const velocity = config.shipVelocity();
    const accel = config.accelMps2();
    if (at === null || velocity === null || finite.sphereKm === null || !(accel > 0)) return planned;
    const gm = config.env.gmOf(plan.destinationId);
    config.env.bodyAt(plan.destinationId, jd, destAt);
    config.env.bodyVelocityAt(plan.destinationId, jd, destVel);
    brakeAt.x = at.x - destAt.x;
    brakeAt.y = at.y - destAt.y;
    brakeAt.z = at.z - destAt.z;
    brakeVel.x = velocity.x - destVel.x;
    brakeVel.y = velocity.y - destVel.y;
    brakeVel.z = velocity.z - destVel.z;
    const r = Math.hypot(brakeAt.x, brakeAt.y, brakeAt.z);
    if (!(r < finite.sphereKm * 1000)) return planned;
    const toPeriapsisS = periapsisInS(brakeAt, brakeVel, gm);
    if (Number.isNaN(toPeriapsisS)) return planned;
    const v = speedOf(brakeVel);
    const vInfMps = Math.sqrt(2 * ((v * v) / 2 - gm / r));
    if (!(Math.abs(vInfMps - leadVInfMps) <= BRAKE_REPRICE_MPS)) {
      const live = finiteCaptureOf(gm / 1e9, finite.parkingRadiusKm, accel / 1000, vInfMps / 1000);
      leadVInfMps = vInfMps;
      leadS = live === null ? finite.leadS : live.capture.leadS;
    }
    return toPeriapsisS - leadS;
  }

  function update(): void {
    const picked = config.plan();
    if (picked === null) {
      if (held !== null) forget();
      return;
    }
    const jd = config.epochJd();
    // A crossing the clock has passed the arrival of is one nothing can be
    // flown of any more: the solve below refuses it, and what the player is
    // about to fly is the window the map has walked on to since.
    const outlived = held !== null && !holdOutlived
      && jd >= held.arrivalEpochJd && picked.arrivalEpochJd > held.arrivalEpochJd;
    // The map hands its own pick on when a window passes with nothing flown on
    // it, and the row owes the crossing offered in its place, not the one that
    // left.
    const handOff = handedOnOf();
    const handedOn = handOff !== null && handOff !== seenHandOff && !holdByArrival;
    seenHandOff = handOff;
    if (heldId !== picked.destinationId || outlived || handedOn) {
      forget();
      held = picked;
      heldId = picked.destinationId;
      const departure = picked.burns.find((b) => b.kind === 'departure') ?? null;
      const originId = departure?.localBurn?.referenceId ?? departure?.referenceId ?? 'sol';
      const ids = ['sol', originId, picked.destinationId]
        .filter((id, i, all) => id !== '' && all.indexOf(id) === i);
      model = createModel(config.env, ids);
      departOriginId = departureOriginId(picked);
      departScheduledJd = scheduledDepartureStartJd(picked, config.accelMps2());
      departStartJd = departScheduledJd;
    }
    const plan = held;
    if (plan === null || model === null) return;
    if (off) return;
    // Something else has the stick: the autopilot flying this same plan, or a
    // fight. The plan is HELD through that rather than dropped, because a
    // player who takes the stick back mid-transfer is still flying the trip
    // they armed and the row is what says whether they are still on it. What
    // does not survive is the answer in hand, which was solved from a ship that
    // has been flown somewhere else since.
    if (!config.live()) {
      solve = null;
      answerMps = 0;
      answerDir = null;
      // The countdown re-times cleanly once live again; a burn already seen
      // does not un-happen just because the stick changed hands.
      departInSValue = Number.NaN;
      departTimedOnce = false;
      departStartJd = departScheduledJd;
      return;
    }

    updateDeparture(plan, jd);

    // Before the plan's own departure there is no course to be off: the row
    // would price a direct shot at the arrival from a parking orbit, which is
    // not what the map planned and not what anybody is flying.
    if (jd < plan.departureEpochJd || jd >= plan.arrivalEpochJd) {
      solve = null;
      answerMps = 0;
      answerDir = null;
      return;
    }
    const at = config.shipAt();
    const velocity = config.shipVelocity();
    if (at === null || velocity === null) return;

    const running = solve;
    if (running !== null) {
      const status = running.advance(stepBudget, msBudget);
      if (status === 'running') return;
      solve = null;
      landedRealMs = performance.now();
      answers++;
      seedJd = solveSeedJd;
      answerMissM = model.lastMissM;
      if (status === 'failed') {
        answerMps = 0;
        answerDir = null;
        return;
      }
      // The answer belongs to the state the solve was SEEDED from, which is
      // where the ship was when it opened: nothing is flying this one, so the
      // clock is not frozen for it the way `Autopilot.freezes` freezes it for a
      // trim, and the ship has coasted on while it ran. What closes that gap is
      // the reading being taken again: the row is re-solved as soon as the
      // answer in hand is a quarter second old or the ship has burned.
      const dx = running.velocity.x - seedVelocity.x;
      const dy = running.velocity.y - seedVelocity.y;
      const dz = running.velocity.z - seedVelocity.z;
      answerMps = Math.hypot(dx, dy, dz);
      answerDir = unit(dx, dy, dz);
      return;
    }

    const speed = speedOf(velocity);
    const moved = Math.abs(speed - seedSpeedMps) > RESOLVE_SPEED_MPS;
    const stale = performance.now() - landedRealMs >= RESOLVE_FLOOR_MS;
    if (!moved && !stale) return;
    aimPoint(plan, jd, at, velocity);
    seedVelocity.x = velocity.x;
    seedVelocity.y = velocity.y;
    seedVelocity.z = velocity.z;
    seedSpeedMps = speed;
    solveSeedJd = jd;
    solve = model.beginRequiredVelocity(at, velocity, jd, aim, plan.arrivalEpochJd);
  }

  function state(): DvCorrectionState {
    if (held === null) return off ? { ...IDLE, off: true } : IDLE;
    const jd = config.epochJd();
    const timeToGoS = Math.max(0, (held.arrivalEpochJd - jd) * SECONDS_PER_DAY);
    if (off) {
      return {
        ...IDLE, armed: true, destinationName: held.destinationName, timeToGoS, off: true,
      };
    }
    const pending = jd < held.departureEpochJd;
    const departure = held.burns.find((b) => b.kind === 'departure') ?? null;
    // The marker holds through the burn and not just up to the epoch: what a
    // pilot aims at while climbing out is where the plan leaves, and the fix
    // the row prices from inside the well is a shot at the arrival taken by a
    // ship that has not left yet.
    const departing = markerEndsAtEpoch
      ? pending
      : jd < departureMarkerEndJd(held, departure, config.accelMps2());
    let marker: DvCorrectionMarker | null = null;
    if (departing) {
      const dir = departure === null
        ? null
        : unit(departure.direction.x, departure.direction.y, departure.direction.z);
      if (dir !== null) marker = { kind: 'departure', dir, label: MARKER_LABELS.departure };
    } else if (answerDir !== null && answerMps > deadbandMps) {
      marker = { kind: 'fix', dir: answerDir, label: MARKER_LABELS.fix };
    }
    const flying = !pending && jd < held.arrivalEpochJd && config.live();
    return {
      armed: true,
      pending,
      destinationName: held.destinationName,
      mps: pending || answerMps <= deadbandMps ? 0 : answerMps,
      timeToGoS,
      brakeInS: flying ? brakeInS(held, jd) : Number.NaN,
      departInS: departInSValue,
      marker,
      solving: solve !== null,
      answers,
      missM: answerMissM,
      seedJd,
      off: false,
    };
  }

  return {
    update,
    state,
    dispose(): void {
      forget();
    },
  };
}
